import {
  decryptInboxMessage,
  encryptInboxMessage,
  encryptInboxMessageWithPublicKey,
} from "../utils/inbox-crypto";

type RemoteRecord = Record<string, unknown>;

interface EncryptTarget {
  inboxPublicKeyHex?: string | null;
  viewingKeyHex?: string | null;
}

interface DecryptContext {
  // Accepts every key the account has been addressed under, newest first, so
  // records written before the inbox was re-keyed still open. See
  // getInboxViewingKeyCandidates / getInboxSecretKeyCandidates.
  viewingKeyHex: string | string[];
  legacyMpkHex?: string;
  inboxSecretKey?: Uint8Array | Uint8Array[];
}

export interface EncryptedPrivateRecord {
  version: number;
  scheme: string;
  ephemeralPubKey: string;
  ciphertext: string;
  iv: string;
  authTag: string;
  recordId?: string;
  recipient?: string;
  spent?: boolean;
  createdAt?: number;
}

function normalizeHex(value: string): string {
  const trimmed = value.trim().toLowerCase();
  return trimmed.startsWith("0x") ? trimmed.slice(2) : trimmed;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

function isObjectRecord(value: unknown): value is RemoteRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isEncryptedPrivateRecord(
  value: unknown,
): value is EncryptedPrivateRecord {
  return (
    isObjectRecord(value) &&
    typeof value.ciphertext === "string" &&
    typeof value.iv === "string" &&
    typeof value.authTag === "string" &&
    typeof value.ephemeralPubKey === "string"
  );
}

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", toArrayBuffer(bytes));
  return Buffer.from(new Uint8Array(digest)).toString("hex");
}

function stableJson(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJson(item)).join(",")}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>).sort(
    ([a], [b]) => a.localeCompare(b),
  );
  return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableJson(item)}`).join(",")}}`;
}

async function buildIdempotencyKey(
  prefix: string,
  parts: unknown[],
): Promise<string> {
  const digest = await sha256Hex(
    `${prefix}:${parts.map((part) => stableJson(part)).join(":")}`,
  );
  return `${prefix}-${digest.slice(0, 48)}`;
}

export async function encryptPrivateRecord(
  payload: unknown,
  target: EncryptTarget,
): Promise<EncryptedPrivateRecord> {
  if (target.inboxPublicKeyHex) {
    const encrypted = await encryptInboxMessageWithPublicKey(
      payload,
      target.inboxPublicKeyHex,
    );
    return {
      version: 1,
      scheme: "inbox-v2-ecdh-aesgcm",
      ...encrypted,
    };
  }

  if (target.viewingKeyHex) {
    const encrypted = await encryptInboxMessage(payload, target.viewingKeyHex);
    return {
      version: 1,
      scheme: "inbox-v1-aesgcm",
      ...encrypted,
    };
  }

  throw new Error("No user encryption target available for private record");
}

export async function decryptPrivateRecord<T>(
  record: EncryptedPrivateRecord,
  decryptContext: DecryptContext,
): Promise<T> {
  return decryptInboxMessage<T>(
    record,
    decryptContext.viewingKeyHex,
    decryptContext.legacyMpkHex,
    decryptContext.inboxSecretKey,
  );
}

async function responseTextOrStatus(response: Response): Promise<string> {
  const text = (await response.text()).trim();
  return text || `HTTP ${response.status}`;
}

export async function buildCommitmentRecordId(
  addressKey: string,
  commitment: string,
): Promise<string> {
  return sha256Hex(
    `stealthos:commitment-record:v1:${addressKey.trim().toLowerCase()}:${normalizeHex(commitment)}`,
  );
}

export async function buildInboxNoteRecordId(
  recipientKey: string,
  commitment: string,
): Promise<string> {
  return sha256Hex(
    `stealthos:note-record:v1:${recipientKey.trim().toLowerCase()}:${normalizeHex(commitment)}`,
  );
}

export async function buildInboxNoteStoreIdempotencyKey(
  recordId: string,
  encryptedNote: {
    ephemeralPubKey: string;
    ciphertext: string;
    iv: string;
    authTag: string;
  },
): Promise<string> {
  return buildIdempotencyKey("notes-store", [
    recordId.trim().toLowerCase(),
    encryptedNote.ephemeralPubKey.trim().toLowerCase(),
    normalizeHex(encryptedNote.ciphertext),
    normalizeHex(encryptedNote.iv),
    normalizeHex(encryptedNote.authTag),
  ]);
}

export async function assertPrivateStorageAccess(params: {
  baseUrl: string;
  recipientKey: string;
  headers: Record<string, string>;
}): Promise<void> {
  const normalizedRecipient = params.recipientKey.trim().toLowerCase();
  const requestHeaders = {
    "Content-Type": "application/json",
    ...params.headers,
  };
  const [notesResponse, commitmentsResponse] = await Promise.all([
    fetch(`${params.baseUrl}/api/v1/notes/${normalizedRecipient}`, {
      headers: requestHeaders,
    }),
    fetch(`${params.baseUrl}/api/v1/commitments/${normalizedRecipient}`, {
      headers: requestHeaders,
    }),
  ]);

  if (!notesResponse.ok) {
    throw new Error(
      `Inbox storage auth check failed: ${await responseTextOrStatus(notesResponse)}`,
    );
  }
  if (!commitmentsResponse.ok) {
    throw new Error(
      `Commitment storage auth check failed: ${await responseTextOrStatus(commitmentsResponse)}`,
    );
  }
}

export async function storePrivateInboxNote(params: {
  baseUrl: string;
  recipientKey: string;
  headers: Record<string, string>;
  encryptedNote: {
    ephemeralPubKey: string;
    ciphertext: string;
    iv: string;
    authTag: string;
  };
  commitment: string;
  treeIndex?: number;
  signal?: AbortSignal;
}): Promise<void> {
  if (
    params.treeIndex !== undefined &&
    (!Number.isSafeInteger(params.treeIndex) ||
      params.treeIndex < 0 ||
      params.treeIndex > 0xffff_ffff)
  ) {
    throw new Error("Inbox note tree index is invalid");
  }
  const recordId = await buildInboxNoteRecordId(
    params.recipientKey,
    params.commitment,
  );
  const idempotencyKey = await buildInboxNoteStoreIdempotencyKey(
    recordId,
    params.encryptedNote,
  );
  const response = await fetch(`${params.baseUrl}/api/v1/notes/store`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...params.headers,
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({
      recipient: params.recipientKey,
      note: {
        ...params.encryptedNote,
        recordId,
        commitment: params.commitment,
        treeIndex: params.treeIndex,
      },
    }),
    signal: params.signal,
  });

  if (!response.ok) {
    throw new Error(await responseTextOrStatus(response));
  }
}

export async function storePrivateCommitment(params: {
  baseUrl: string;
  addressKey: string;
  headers: Record<string, string>;
  commitment: RemoteRecord;
  encryptTarget: EncryptTarget;
}): Promise<void> {
  const recordId = await buildCommitmentRecordId(
    params.addressKey,
    String(params.commitment.commitment || ""),
  );
  const encrypted = await encryptPrivateRecord(
    params.commitment,
    params.encryptTarget,
  );
  const payload = {
    ...encrypted,
    recordId,
  };

  const response = await fetch(
    `${params.baseUrl}/api/v1/commitments/${params.addressKey}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...params.headers },
      body: JSON.stringify({ commitment: payload }),
    },
  );

  if (!response.ok) {
    throw new Error(await responseTextOrStatus(response));
  }
}

export async function markPrivateCommitmentSpent(params: {
  baseUrl: string;
  addressKey: string;
  commitment: string;
  headers: Record<string, string>;
  spent?: boolean;
}): Promise<void> {
  const recordId = await buildCommitmentRecordId(
    params.addressKey,
    params.commitment,
  );
  const response = await fetch(
    `${params.baseUrl}/api/v1/commitments/${params.addressKey}/${params.commitment}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...params.headers },
      body: JSON.stringify({
        spent: params.spent ?? true,
        recordId,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(await responseTextOrStatus(response));
  }
}

export async function fetchPrivateCommitments(params: {
  baseUrl: string;
  addressKey: string;
  headers: Record<string, string>;
  decryptContext: DecryptContext;
  throwOnHttpError?: boolean;
}): Promise<RemoteRecord[]> {
  const response = await fetch(
    `${params.baseUrl}/api/v1/commitments/${params.addressKey}`,
    {
      headers: { "Content-Type": "application/json", ...params.headers },
    },
  );

  if (!response.ok) {
    if (params.throwOnHttpError) {
      throw new Error(await responseTextOrStatus(response));
    }
    return [];
  }

  const data = (await response.json()) as { commitments?: unknown[] };
  const commitments: RemoteRecord[] = [];

  for (const item of data.commitments || []) {
    if (!isObjectRecord(item)) {
      continue;
    }

    if (!isEncryptedPrivateRecord(item)) {
      commitments.push(item);
      continue;
    }

    try {
      const decrypted = await decryptPrivateRecord<RemoteRecord>(
        item,
        params.decryptContext,
      );
      commitments.push({
        ...decrypted,
        spent:
          typeof item.spent === "boolean"
            ? item.spent
            : Boolean(decrypted.spent),
        timestamp: decrypted.timestamp ?? item.createdAt,
      });
    } catch (error) {
      console.warn(
        "[private-data-storage] Failed to decrypt commitment record",
        error,
      );
    }
  }

  return commitments;
}

export async function storePrivateHistoryEntry(params: {
  baseUrl: string;
  headers: Record<string, string>;
  entry: RemoteRecord;
  encryptTarget: EncryptTarget;
}): Promise<void> {
  const encrypted = await encryptPrivateRecord(
    // Keep the event time inside the encrypted payload. The server stores only
    // ciphertext and insertion order, so it cannot reconstruct this later.
    { ...params.entry, timestamp: params.entry.timestamp ?? Date.now() },
    params.encryptTarget,
  );
  const recipient = String(params.entry.recipient || "");
  const idempotencyKey = await buildIdempotencyKey("history-store", [
    recipient,
    params.entry,
  ]);

  const response = await fetch(`${params.baseUrl}/api/v1/history/store`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...params.headers,
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({ recipient, entry: encrypted }),
  });

  if (!response.ok) {
    throw new Error(await responseTextOrStatus(response));
  }
}

async function readPrivateHistory(params: {
  baseUrl: string;
  recipientKey: string;
  headers: Record<string, string>;
  decryptContext: DecryptContext;
  before?: string | null;
  limit?: number;
}): Promise<{ entries: RemoteRecord[]; nextCursor: string | null }> {
  const query = params.limit
    ? `?limit=${params.limit}${params.before ? `&before=${encodeURIComponent(params.before)}` : ""}`
    : "";
  const response = await fetch(
    `${params.baseUrl}/api/v1/history/${params.recipientKey}${query}`,
    {
      headers: { "Content-Type": "application/json", ...params.headers },
      signal: AbortSignal.timeout(20_000),
    },
  );

  if (!response.ok) {
    throw new Error(await responseTextOrStatus(response));
  }

  const data = (await response.json()) as {
    entries?: unknown[];
    nextCursor?: string | null;
  };
  const entries: RemoteRecord[] = [];

  for (const item of data.entries || []) {
    if (!isObjectRecord(item)) {
      continue;
    }

    if (!isEncryptedPrivateRecord(item)) {
      entries.push(item);
      continue;
    }

    try {
      const decrypted = await decryptPrivateRecord<RemoteRecord>(
        item,
        params.decryptContext,
      );
      entries.push({
        ...decrypted,
        recipient: decrypted.recipient ?? item.recipient ?? params.recipientKey,
        timestamp: decrypted.timestamp ?? item.createdAt,
      });
    } catch (error) {
      console.warn(
        "[private-data-storage] Failed to decrypt history record",
        error,
      );
    }
  }

  if (
    params.limit &&
    data.nextCursor !== null &&
    (typeof data.nextCursor !== "string" ||
      !/^[1-9][0-9]*$/.test(data.nextCursor))
  )
    throw Error("Invalid history cursor");
  return { entries, nextCursor: data.nextCursor ?? null };
}

export async function fetchPrivateHistoryEntries(
  params: Parameters<typeof readPrivateHistory>[0],
): Promise<RemoteRecord[]> {
  return (await readPrivateHistory(params)).entries;
}

export function fetchPrivateHistoryPage(
  params: Parameters<typeof readPrivateHistory>[0],
) {
  return readPrivateHistory({ ...params, limit: 25 });
}
